import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormLabel,
  Select,
  SelectProps,
} from '@chakra-ui/react';
import {
  FieldPathByValue,
  FieldValues,
  get,
  useFormContext,
} from 'react-hook-form';

type FormSelectProps<
  FormInputs extends FieldValues,
  OptValueType extends string,
> = {
  inputPath: FieldPathByValue<FormInputs, OptValueType>;
  required?: boolean;
  placeholder?: string;
  options: Array<{ value: OptValueType; label: string }>;
  label?: string;
  sx?: FormControlProps;
  selectProps?: SelectProps;
};

export function FormSelect<
  FormInputs extends FieldValues,
  OptValueType extends string,
>({
  inputPath,
  required,
  placeholder,
  options,
  label,
  sx,
  selectProps,
}: FormSelectProps<FormInputs, OptValueType>) {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormInputs>();

  const error = get(errors, inputPath);

  return (
    <FormControl isInvalid={!!error} {...sx}>
      {label && (
        <FormLabel
          htmlFor={inputPath}
          color="text.muted"
          fontWeight="medium"
          mb={2}
          fontSize="sm">
          {label}
        </FormLabel>
      )}
      <Select
        {...register(inputPath, {
          required: required ? 'Required' : false,
        })}
        placeholder={placeholder}
        bg="surface.hover"
        borderColor="transparent"
        borderRadius="md"
        _placeholder={{ color: 'gray.400' }}
        _focus={{
          borderColor: 'blue.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
        }}
        _hover={{
          borderColor: 'border.box',
        }}
        {...selectProps}>
        {options.map(({ value, label: optionLabel }) => (
          <option key={value} value={value}>
            {optionLabel}
          </option>
        ))}
      </Select>
      {error?.message && typeof error.message === 'string' && (
        <FormErrorMessage>{error.message}</FormErrorMessage>
      )}
    </FormControl>
  );
}
